import { useState, useEffect } from 'react';
import { useWorkerContext } from '../contexts/WorkerContext';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Button,
  Chip,
  OutlinedInput,
} from '@mui/material';

interface MatchedPartner {
  partner_name: string;
  account_name: string;
  match_score: number;
}

interface PartnerSuggestion {
  opportunity_id: string;
  opportunity_account: string;
  opportunity_name: string;
  opportunity_owner: string;
  opportunity_owner__manager: string;
  fiscal_period: string;
  stage: string;
  amount: number;
  matched_partners: MatchedPartner[];
}

export default function PartnerSuggestions() {
  const { worker } = useWorkerContext();
  const [suggestions, setSuggestions] = useState<PartnerSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasFilters, setHasFilters] = useState(false);
  const [filters, setFilters] = useState({
    opportunity_owner__manager: '',
    opportunity_owner: '',
    fiscal_period: '',
    stages: [] as string[],
    partners: [] as string[],
  });
  
  const [uniqueManagers, setUniqueManagers] = useState<string[]>([]);
  const [uniqueOwners, setUniqueOwners] = useState<string[]>([]);
  const [uniquePeriods, setUniquePeriods] = useState<string[]>([]);
  const [uniqueStages, setUniqueStages] = useState<string[]>([]);
  const [uniquePartners, setUniquePartners] = useState<string[]>([]);

  // Fetch filter options on initial load
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // This endpoint should be created to only return filter options
        const options = await worker.getPartnerSuggestionFilters();
        setUniqueManagers(options.managers || []);
        setUniqueOwners(options.owners || []);
        setUniquePeriods(options.periods || []);
        setUniqueStages(options.stages || []);
        setUniquePartners(options.partners || []);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchFilterOptions();
  }, [worker]);

  // Store the original filter options for reference
  const [originalFilterOptions, setOriginalFilterOptions] = useState({
    managers: [] as string[],
    owners: [] as string[],
    periods: [] as string[],
    stages: [] as string[],
    partners: [] as string[]
  });

  // Save original filter options when they're first loaded
  useEffect(() => {
    if (uniqueManagers.length > 0 && originalFilterOptions.managers.length === 0) {
      setOriginalFilterOptions({
        managers: [...uniqueManagers],
        owners: [...uniqueOwners],
        periods: [...uniquePeriods],
        stages: [...uniqueStages],
        partners: [...uniquePartners]
      });
    }
  }, [uniqueManagers, uniqueOwners, uniquePeriods, uniqueStages, uniquePartners]);

  // Check if any filters are applied
  useEffect(() => {
    const hasAnyFilter = 
      filters.opportunity_owner__manager !== '' || 
      filters.opportunity_owner !== '' || 
      filters.fiscal_period !== '' ||
      filters.stages.length > 0 ||
      filters.partners.length > 0;
    
    setHasFilters(hasAnyFilter);
  }, [filters]);

  const handleFilterChange = (event: SelectChangeEvent<string>, filterKey: 'opportunity_owner__manager' | 'opportunity_owner' | 'fiscal_period') => {
    const newValue = event.target.value;
    setFilters(prev => ({ ...prev, [filterKey]: newValue }));
    
    // Reset the dropdown options to the original full list when a filter is cleared
    if (newValue === '') {
      if (filterKey === 'opportunity_owner__manager') {
        setUniqueManagers(originalFilterOptions.managers);
      } else if (filterKey === 'opportunity_owner') {
        setUniqueOwners(originalFilterOptions.owners);
      } else if (filterKey === 'fiscal_period') {
        setUniquePeriods(originalFilterOptions.periods);
      }
    }
  };

  const handleStageChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFilters(prev => ({
      ...prev,
      stages: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handlePartnerChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFilters(prev => ({
      ...prev,
      partners: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleSearch = async () => {
    if (!hasFilters) return;
    
    setLoading(true);
    try {
      const result = await worker.getPartnerSuggestions(filters);
      setSuggestions(result);
      
      // We don't update the filter dropdowns here anymore
      // This ensures all options remain available regardless of the current results
    } catch (error) {
      console.error('Error fetching partner suggestions:', error);
    }
    setLoading(false);
  };
  
  // Add a reset filters function
  const handleResetFilters = () => {
    setFilters({
      opportunity_owner__manager: '',
      opportunity_owner: '',
      fiscal_period: '',
      stages: [] as string[],
      partners: [] as string[],
    });
    
    // Reset all dropdown options to their original values
    setUniqueManagers(originalFilterOptions.managers);
    setUniqueOwners(originalFilterOptions.owners);
    setUniquePeriods(originalFilterOptions.periods);
    setUniqueStages(originalFilterOptions.stages);
    setUniquePartners(originalFilterOptions.partners);
  };

  const renderFilterPanel = () => (
    <Box display="flex" flexDirection="column" gap={3} mb={3}>
      <Box display="flex" gap={2}>
        <FormControl fullWidth>
          <InputLabel>Manager</InputLabel>
          <Select
            value={filters.opportunity_owner__manager}
            label="Manager"
            onChange={(e) => handleFilterChange(e, 'opportunity_owner__manager')}
          >
            <MenuItem value="">All Managers</MenuItem>
            {uniqueManagers.map((manager) => (
              <MenuItem key={manager} value={manager}>
                {manager}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Owner</InputLabel>
          <Select
            value={filters.opportunity_owner}
            label="Owner"
            onChange={(e) => handleFilterChange(e, 'opportunity_owner')}
          >
            <MenuItem value="">All Owners</MenuItem>
            {uniqueOwners.map((owner) => (
              <MenuItem key={owner} value={owner}>
                {owner}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Fiscal Period</InputLabel>
          <Select
            value={filters.fiscal_period}
            label="Fiscal Period"
            onChange={(e) => handleFilterChange(e, 'fiscal_period')}
          >
            <MenuItem value="">All Periods</MenuItem>
            {uniquePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {period}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box display="flex" gap={2} alignItems="center">
        <FormControl fullWidth>
          <InputLabel>Stage</InputLabel>
          <Select
            multiple
            value={filters.stages}
            onChange={handleStageChange}
            input={<OutlinedInput label="Stage" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            )}
          >
            {uniqueStages.map((stage) => (
              <MenuItem key={stage} value={stage}>
                {stage}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box display="flex" gap={2} alignItems="center">
        <FormControl fullWidth>
          <InputLabel>Partners</InputLabel>
          <Select
            multiple
            value={filters.partners}
            onChange={handlePartnerChange}
            input={<OutlinedInput label="Partners" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            )}
          >
            {uniquePartners.map((partner) => (
              <MenuItem key={partner} value={partner}>
                {partner}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSearch}
          disabled={!hasFilters}
          sx={{ height: '56px', minWidth: '120px' }}
        >
          Search
        </Button>
        
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleResetFilters}
          sx={{ height: '56px', minWidth: '120px' }}
        >
          Reset Filters
        </Button>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ typography: 'h4', mb: 3 }}>Partner Suggestions</Box>
      
      {renderFilterPanel()}
      
      {!hasFilters && (
        <Box 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1
          }}
        >
          <Box sx={{ typography: 'h6', mb: 2 }}>Please Select at Least One Filter</Box>
          <Box sx={{ color: 'text.secondary' }}>
            To improve performance and see partner suggestions, please select a Manager, Owner, Fiscal Period, or Stage filter.
          </Box>
        </Box>
      )}
      
      {hasFilters && suggestions.length === 0 && (
        <Box 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1
          }}
        >
          <Box sx={{ typography: 'h6', mb: 2 }}>No Results Found</Box>
          <Box sx={{ color: 'text.secondary' }}>
            Try different filter criteria or click the Search button to find partner suggestions.
          </Box>
        </Box>
      )}

      {suggestions.length > 0 && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="partner suggestions table">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Opportunity</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Suggested Partners</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suggestions.map((row) => (
                <TableRow
                  key={row.opportunity_id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.opportunity_account}
                  </TableCell>
                  <TableCell>{row.opportunity_name}</TableCell>
                  <TableCell>{row.opportunity_owner}</TableCell>
                  <TableCell>{row.opportunity_owner__manager}</TableCell>
                  <TableCell>{row.fiscal_period}</TableCell>
                  <TableCell>{row.stage}</TableCell>
                  <TableCell align="right">${row.amount?.toLocaleString() || 0}</TableCell>
                  <TableCell>
                    {row.matched_partners && row.matched_partners.length > 0 ? (
                      <ul style={{ margin: 0, paddingInlineStart: '20px' }}>
                        {row.matched_partners.map((partner, index) => (
                          <li key={index}>
                            {partner.partner_name} ({partner.account_name}) - {partner.match_score}%
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span style={{ color: 'gray' }}>No partners found</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
